import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import DOMPurify from 'dompurify';

@Pipe({ name: 'safeUrl' })
export class SafeUrlPipe implements PipeTransform {
  constructor(private sanitizer: DomSanitizer) {}

  transform(url: string): SafeResourceUrl {
    const decodedUrl = decodeURIComponent(url);
    const sanitizedUrl = this.sanitizeUrl(decodedUrl);

    if (sanitizedUrl) {
      return this.sanitizer.bypassSecurityTrustResourceUrl(sanitizedUrl);
    }

    return this.sanitizer.bypassSecurityTrustResourceUrl('');
  }

  /**
   * Sanitizes the given URL using DOMPurify to remove potentially harmful content.
   *
   * This method first sanitizes the URL to ensure it doesn't contain any malicious content,
   * such as JavaScript code or invalid elements. After sanitization, the URL is parsed to
   * check if it is valid and whether its protocol is allowed (e.g., `http`, `https`, `ftp`).
   *
   * @param url - The URL to be sanitized.
   * @returns The sanitized URL if valid and safe, otherwise `null`.
   */
  private sanitizeUrl(url: string): string | null {
    try {
      const sanitized = DOMPurify.sanitize(url, { ALLOWED_URI_REGEXP: /^https?|^ftp/ });
      const parsedUrl = new URL(sanitized);
      if (this.isAllowedProtocol(parsedUrl.protocol)) {
        return sanitized;
      }
    } catch (e) {
      console.error('Invalid URL:', url, 'Error:', e);
      return null;
    }
    return null;
  }

  /**
   * Checks if the protocol of a given URL is allowed.
   *
   * This method ensures that only URLs with certain protocols (e.g., `http`, `https`, `ftp`)
   * are considered safe. This helps prevent the execution of harmful protocols such as `javascript:`.
   *
   * @param protocol - The protocol to be checked (e.g., `http:`, `https:`, `ftp:`).
   * @returns `true` if the protocol is allowed, otherwise `false`.
   */
  private isAllowedProtocol(protocol: string): boolean {
    const allowedProtocols = ['http:', 'https:', 'ftp:'];
    return allowedProtocols.includes(protocol);
  }
}
